import { useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search } from 'lucide-react'
import type { SortField, AdminStatusFilter } from '../../hooks/useAdminFilters'
import { statusOptions } from '../../hooks/useAdminFilters'
import { useMediaQuery } from '../../hooks/useMediaQuery'

interface Props {
  search: string
  setSearch: (v: string) => void
  statusFilter: AdminStatusFilter
  setStatusFilter: (v: AdminStatusFilter) => void
  sortField: SortField
  toggleSort: (field: SortField) => void
  sortIcon: (field: SortField) => string
  hasActiveFilters: boolean
  clearFilters: () => void
  totalCount: number
  filteredCount: number
}

export default function AdminFiltersBar({
  search, setSearch,
  statusFilter, setStatusFilter,
  sortField, toggleSort, sortIcon,
  hasActiveFilters, clearFilters,
  totalCount, filteredCount,
}: Props) {
  const navigate   = useNavigate()
  const searchRef  = useRef<HTMLInputElement>(null)
  const isMobile = useMediaQuery('(max-width: 639px)')

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      if (e.key === '=' || e.key === '+') { e.preventDefault(); navigate('/admin/add') }
      if (e.key === 'Backspace')          { e.preventDefault(); navigate('/') }
      if (e.key === '/' || e.key === 'Shift') { e.preventDefault(); searchRef.current?.focus() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [navigate])

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-6 px-4">
        <div>
          <h1 className="font-serif text-[28px] lg:text-[32px] font-bold text-text-primary tracking-tight leading-none mb-1">
            ניהול מוצרים
          </h1>
          <p className="text-sm text-text-muted">
            {filteredCount} מתוך {totalCount} מוצרים
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5 items-center px-4">
        <div className="relative flex-1 min-w-[250px]">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted pointer-events-none" />
          <input
            ref={searchRef}
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Escape') { setSearch(''); e.currentTarget.blur() }
            }}
            placeholder={isMobile ? "חיפוש..." : "Shift / Esc  חיפוש..."}
            dir="rtl"
            aria-label="חיפוש מוצרים"
            className="w-full py-2.5 pr-9 pl-4 rounded-[10px] border-[1.5px] border-cream-dark bg-white text-sm text-text-primary placeholder:text-text-muted outline-none transition-all focus:border-green-primary focus:shadow-[0_0_0_3px_rgba(45,106,79,0.10)]"
          />
        </div>

        <select
          id="statusFilter"
          name="statusFilter"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as AdminStatusFilter)}
          className="h-[42px] px-5 lg:px-3 rounded-[10px] border-[1.5px] border-cream-dark bg-white text-md lg:text-sm text-text-secondary outline-none focus:border-green-primary cursor-pointer appearance-none"
        >
          {statusOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        

        <button
          onClick={() => toggleSort('price')}
          className={`py-2.5 px-3 rounded-[10px] border-[1.5px] text-sm cursor-pointer transition-all ${
            sortField === 'price'
              ? 'border-green-primary bg-green-muted text-green-primary font-semibold'
              : 'border-cream-dark bg-white text-text-secondary hover:border-green-primary'
          }`}
        >
          מחיר{sortIcon('price')}
        </button>

        <button
          onClick={() => toggleSort('quantity')}
          className={`py-2.5 px-3 rounded-[10px] border-[1.5px] text-sm cursor-pointer transition-all ${
            sortField === 'quantity'
              ? 'border-green-primary bg-green-muted text-green-primary font-semibold'
              : 'border-cream-dark bg-white text-text-secondary hover:border-green-primary'
          }`}
        >
          כמות{sortIcon('quantity')}
        </button>

        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="py-2.5 px-3 rounded-[10px] border-[1.5px] border-[#FFE8E0] bg-white text-sm text-[#C0392B] cursor-pointer hover:bg-[#FFE8E0] transition-colors"
          >
            נקה
          </button>
        )}
        {/* <button
          onClick={() => window.open('/design-system.html', '_blank')}
          className="flex items-center gap-2 px-4 py-2 rounded-[10px] text-sm font-medium text-text-secondary bg-white border-none cursor-pointer shadow-[0_2px_8px_rgba(44,24,16,0.07)] hover:shadow-[0_4px_16px_rgba(44,24,16,0.12)] transition-shadow"
        >
          Design System
        </button> */}

      </div>


      {/* Table header — desktop only */}
      <div className="hidden lg:flex items-center px-8 pb-2 text-xs font-semibold text-text-muted">
        <span className="w-12" />
        <span className="flex-1 text-right">מוצר</span>
        <span className="w-41 text-center">סטטוס</span>
        <span className="w-1 text-center">כמות</span>
        <span className="w-54 text-center">שינוי סטטוס</span>
        <span className="w-14 text-center">פעולות</span>
      </div>

    </>
  )
}
